/* eslint indent: 0 */

import React from 'react'

import PropTypes from 'prop-types'
import {
  Modal,
  ModalHeader,
  ModalFooter,
  Button,
  lightTheme,
  ThemeProvider,
  DatePickerRange as DatePickerRangeBase,
  Tags,
  CopyFromProject,
  Tooltip,
  Icon,
  Typography,
  NoOptionFound
} from '@rodeo-dev/legos'
import { ClientSearch } from './ClientSearch.jsx'
import { ProjectForm } from './ProjectForm.jsx'
import * as Wrappers from './Wrappers.jsx'
import {
  convertToCopyOptionsArray,
  getDefaultCopyOptionsState,
  getNewCopyOptionState,
  getDateFormat,
  isInArrayByKey,
  getErrorMessages
} from './misc'
import IntlUtil from '../../../lib/intlutil.jsx'

const DatePickerRange = React.memo(DatePickerRangeBase)

const isValidForm = obj => !Object.entries(obj).find(([, v]) => v)

export class AddProjectModal extends React.Component {
  constructor (props) {
    super(props)

    this.defaultOptionsState = getDefaultCopyOptionsState({
      isPmVisible: props.isPmVisible,
      isPlannerAllowed: props.isPlannerAllowed
    })

    this.state = {
      isOpenModal: props.isOpen,
      errors: { dates: '' },
      projectDates: {},
      isCopyProject: false,
      copyClient: {},
      copyClientProjects: [],
      copyProject: null,
      projectTitle: '',
      projectNumber: '',
      tags: [],
      activeClients: [],
      selectedClient: null,
      clientProjects: [],
      nextProjectNumberSuggestion: '',
      copyOptions: this.defaultOptionsState,
      itemsToCopy: convertToCopyOptionsArray(this.defaultOptionsState),
      errorMessages: getErrorMessages()
    }
  }

  componentDidMount () {
    // load active clients
    this.props.getActiveClients(activeClients => this.updateState({ activeClients }))
  }

  updateErrors = newErrors =>
    this.setState({ errors: { ...this.state.errors, ...newErrors } })

  updateState = (state, newErrors) => {
    const errors = newErrors
      ? { errors: { ...this.state.errors, ...newErrors } }
      : {}
    this.setState({ ...state, ...errors })
  }

  handleModalClose = () => {
    this.updateState({ isOpenModal: false })
    this.props.onModalClose()
  }

  handleProjectTitle = title => {
    this.updateState({ projectTitle: title }, { title: '' })
  }

  handleProjectNumber = number => {
    this.updateState({ projectNumber: number }, { projectNumber: '' })
  }

  handleClientSelect = client => {
    const {
      isNumberAutoGenerated,
      getProjectNumber,
      getClientProjects
    } = this.props

    let newState = {}

    if (client) {
      // fetch project number fom API
      !isNumberAutoGenerated &&
        getProjectNumber(
          isNumberAutoGenerated,
          client.id,
          nextProjectNumberSuggestion =>
            this.updateState({ nextProjectNumberSuggestion })
        )
      // fetch list of  projects  fom API
      getClientProjects(client.id, projectList => {
        this.updateState({
          copyClient: client,
          clientProjects: projectList,
          copyClientProjects: projectList
        })
      })
    } else {
      // clear project number field, suggestion and copyOption
      newState = {
        copyProject: false,
        nextProjectNumberSuggestion: null,
        copyOptions: this.defaultOptionsState
      }
    }

    this.updateState(
      {
        selectedClient: client,
        ...newState
      },
      { client: '' }
    )
  }

  handleCreateNewClient = typedClientName => {
    this.props.onCreateClient(typedClientName, clientId =>
      this.updateState({
        activeClients: [
          ...this.state.activeClients,
          { name: typedClientName, id: clientId }
        ]
      })
    )
  }

  handleOnClientToCopyFormChange = client => {
    if (client) {
      this.props.getClientProjects(client.id, copyClientProjects => {
        this.updateState(
          {
            copyClientProjects,
            copyClient: client,
            copyProject: null
          },
          {
            copyClient: ''
          }
        )
      })
    } else {
      // clean list of copy projects
      this.updateState(
        {
          copyClientProjects: []
        },
        {
          copyClient: this.state.errorMessages.mandatory
        }
      )
    }
  }

  handleItemsToCopyChange = item => {
    const newOptionsState = getNewCopyOptionState(
      item.key,
      this.state.copyOptions
    )
    this.updateState({
      copyOptions: newOptionsState,
      itemsToCopy: convertToCopyOptionsArray(newOptionsState)
    })
  }

  handlerDateErrors = e => {
    // because the rangePicker fires twice the errors. here we convert the error to a boolean
    // so we can catch the error as one error
    this.state.errors.dates !== !!e && this.updateErrors({ dates: !!e })
  }

  validateForm = () => {
    const {
      clientProjects,
      projectTitle,
      selectedClient,
      projectNumber,
      isCopyProject,
      copyClient,
      copyProject,
      errors
    } = this.state
    const { isNumberSupported } = this.props

    const titleError = isInArrayByKey(clientProjects, 'title', projectTitle)
      ? this.state.errorMessages.existingProject
      : !projectTitle && this.state.errorMessages.mandatory

    return {
      ...errors,
      client: !selectedClient && this.state.errorMessages.mandatory,
      title: titleError,
      projectNumber:
        isNumberSupported &&
        !projectNumber &&
        this.state.errorMessages.mandatory,
      copyClient:
        isCopyProject && !copyClient && this.state.errorMessages.mandatory,
      copyProject:
        isCopyProject && !copyProject && this.state.errorMessages.mandatory
    }
  }

  handleSaveProject = () => {
    const {
      isCopyProject,
      copyProject,
      copyOptions,
      selectedClient,
      projectTitle,
      projectNumber,
      projectDates,
      tags
    } = this.state
    const { onSaveProject } = this.props

    const newErrors = this.validateForm()
    this.updateErrors({ ...newErrors })
    const copyData = isCopyProject
      ? {
          projectId: copyProject && copyProject.id,
          subObjects: Object.entries(copyOptions)
            .filter(([, v]) => v.checked)
            .map(([, v]) => v.identifier)
        }
      : {}

    isValidForm(newErrors) &&
      onSaveProject(
        {
          contactId: selectedClient.id,
          title: projectTitle,
          projectNumber,
          ...projectDates,
          tags
        },
        copyData
      )
  }

  handleTagsUpdate = tags => this.updateState({ tags })

  render () {
    const {
      errors,
      isOpenModal,
      activeClients,
      nextProjectNumberSuggestion,
      clientProjects,
      copyClient,
      copyClientProjects,
      itemsToCopy,
      selectedClient
    } = this.state
    const {
      isNumberAutoGenerated,
      isNumberSupported,
      nextProjectNumber,
      language,
      tagSuggestions
    } = this.props

    return (
      <ThemeProvider theme={lightTheme}>
        <Modal open={isOpenModal} width={560} onClose={this.handleModalClose}>
          <ModalHeader onClose={this.handleModalClose}>
            <span>{IntlUtil.convertMessage('project.create.title')}</span>
          </ModalHeader>
          <Wrappers.ModalBody maxHeight='75vh'>
            <Wrappers.FromItem noMarginTop>
              <ClientSearch
                clientList={activeClients}
                onClientSelect={this.handleClientSelect}
                onCreateNewClient={this.handleCreateNewClient}
                error={errors.client}
              />
            </Wrappers.FromItem>
            <Wrappers.FromItem noMarginTop>
              <ProjectForm
                isNumberSupported={isNumberSupported}
                isNumberAutoGenerated={isNumberAutoGenerated}
                nextProjectNumber={nextProjectNumber}
                nextProjectNumberSuggestion={nextProjectNumberSuggestion}
                projectList={clientProjects}
                onTitleChange={this.handleProjectTitle}
                onNumberChange={this.handleProjectNumber}
                errors={{
                  title: errors.title,
                  projectNumber: errors.projectNumber
                }}
              />
            </Wrappers.FromItem>
            <Wrappers.FromItem>
              <DatePickerRange
                startDateLabel={IntlUtil.convertMessage(
                  'project.create.projectStarts'
                )}
                endDateLabel={IntlUtil.convertMessage(
                  'project.create.projectEnds'
                )}
                onChange={dates => this.updateState({ projectDates: dates })}
                format={getDateFormat(language)}
                onError={this.handlerDateErrors} // eslint-disable-line react/jsx-handler-names
                placeholder={getDateFormat(language).toUpperCase()}
                lang={language}
              />
            </Wrappers.FromItem>
            {selectedClient && (
              <Wrappers.FromItemWithTooltip>
                <Wrappers.CopyProject>
                  <CopyFromProject
                    title={IntlUtil.convertMessage(
                      'project.create.copyFromAProject'
                    )}
                    clientInputLabel={IntlUtil.convertMessage(
                      'project.create.clientName'
                    )}
                    projectInputLabel={IntlUtil.convertMessage(
                      'project.list.orderByProjectName'
                    )}
                    defaultClient={copyClient}
                    clientList={activeClients}
                    projectList={copyClientProjects}
                    itemsToCopy={itemsToCopy}
                    onItemsToCopyChange={this.handleItemsToCopyChange}
                    onClientChange={this.handleOnClientToCopyFormChange}
                    onProjectChange={copyProject => {
                      this.updateState({ copyProject }, { copyProject: '' })
                    }}
                    clientInputProps={{
                      getOptionLabel: option => option.name,
                      inputProps: {
                        required: true,
                        error: !!errors.copyClient,
                        helperText: errors.copyClient
                      }
                    }}
                    projectInputProps={{
                      noOptionsText: (
                        <NoOptionFound
                          headerLabel={IntlUtil.convertMessage(
                            'project.create.notFoundOptionHeaderLabel'
                          )}
                          subHeaderLabel={IntlUtil.convertMessage(
                            'project.create.projectNotFound'
                          )}
                        />
                      ),
                      inputProps: {
                        required: true,
                        error: !!errors.copyProject,
                        helperText: errors.copyProject
                      }
                    }}
                    onCollapseToggle={isCopyProject => {
                      this.updateState({ isCopyProject })
                    }}
                  />
                </Wrappers.CopyProject>
                <Wrappers.Tooltip>
                  <Tooltip
                    placement='right-start'
                    variant='light'
                    title={
                      <Typography variant='body1'>
                        {IntlUtil.convertMessage(
                          'project.create.tooltipCopyFromProject'
                        )}
                      </Typography>
                    }
                  >
                    <Icon
                      color='action'
                      fontSize='small'
                      prefix='fas'
                      icon='info-circle'
                    />
                  </Tooltip>
                </Wrappers.Tooltip>
              </Wrappers.FromItemWithTooltip>
            )}
            <Wrappers.FromItemWithTooltip>
              <Wrappers.Tags>
                <Tags
                  id='fullWidth-tags'
                  fullWidth
                  options={tagSuggestions}
                  onUpdate={this.handleTagsUpdate}
                  label={IntlUtil.convertMessage('project.create.addTag')}
                  placeholder={IntlUtil.convertMessage('project.create.addTag')}
                />
              </Wrappers.Tags>
              <Wrappers.Tooltip>
                <Tooltip
                  placement='right-start'
                  variant='light'
                  title={
                    <Typography variant='body1'>
                      {IntlUtil.convertMessage('project.create.tooltipTags')}
                    </Typography>
                  }
                >
                  <Icon
                    color='action'
                    fontSize='small'
                    prefix='fas'
                    icon='info-circle'
                  />
                </Tooltip>
              </Wrappers.Tooltip>
            </Wrappers.FromItemWithTooltip>
          </Wrappers.ModalBody>
          <ModalFooter>
            <Button
              size='large'
              color='secondary'
              onClick={this.handleSaveProject}
            >
              {IntlUtil.convertMessage('project.create.createProject')}
            </Button>
          </ModalFooter>
        </Modal>
      </ThemeProvider>
    )
  }
}

AddProjectModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onModalClose: PropTypes.func.isRequired,
  onCreateClient: PropTypes.func.isRequired,
  getActiveClients: PropTypes.func.isRequired,
  getProjectNumber: PropTypes.func.isRequired,
  getClientProjects: PropTypes.func.isRequired,
  onSaveProject: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  isPmVisible: PropTypes.bool,
  isPlannerAllowed: PropTypes.bool,
  isNumberAutoGenerated: PropTypes.bool,
  isNumberSupported: PropTypes.bool,
  nextProjectNumber: PropTypes.string,
  tagSuggestions: PropTypes.array
}
